// Hợp đồng manifest pack — phần THUẦN của `pack-manifest-store.ts`: tên file, hình
// dạng dữ liệu, và phép đọc/validate từ chuỗi JSON. KHÔNG chạm filesystem.
//
// Vì sao tách khỏi `pack-manifest-store.ts` (#292):
//   Extension VSCode cần đúng phần kiến thức này (biết manifest tên gì, có field
//   gì) nhưng đọc file bằng `fs.readFileSync` ĐỒNG BỘ — nó gọi trong constructor
//   của `VersionCheckController`, đổi sang async sẽ lan ra cả đường `activate()`.
//   `pack-manifest-store.ts` lại import `filesystem-helpers.js`, mà file đó kéo
//   theo `node:crypto` + `node:child_process` + cả đường tải/giải nén tarball.
//   Nhét nguyên cụm đó vào bundle extension là vô cớ, nên tách hợp đồng thuần ra
//   đây: một bản implement cho kiến thức, hai lớp vỏ IO (async ở CLI, sync ở
//   extension).
//
// ⛔ KHÔNG thêm import `node:*` vào file này — nó nằm trong `src/core/index.ts`
// (mặt tiền library) và phải bundle được vào extension mà không kéo theo gì.

export const PACK_MANIFEST_FILENAME = ".avatar-pack-manifest.json";
export const PACK_VERSION_FILENAME = ".pack-version";

export interface PackManifest {
  version: string; // vd "v0.2.0"
  downloadedObject: string; // vd "pack-v0.2.0.tar.gz"
  sha256?: string; // hash tarball đã tải (nếu Supabase cung cấp)
  extractedAt: string; // ISO timestamp
}

/**
 * Hình dạng ĐỌC được từ đĩa — khác hình dạng GHI ở chỗ chỉ `version` được kiểm.
 *
 * Vì sao không dùng thẳng `PackManifest`: manifest do bản CLI cũ (hoặc pack cài
 * tay) ghi có thể thiếu `downloadedObject`/`extractedAt`. Siết đủ 3 field sẽ biến
 * những manifest đó thành "không có manifest" một cách IM LẶNG. Nhưng khai kiểu
 * trả về là `PackManifest` thì type nói dối: TypeScript hứa `downloadedObject` là
 * `string` trong khi runtime có thể `undefined`. Tách kiểu ra là cách giữ cả hai —
 * đường GHI vẫn bị bắt buộc đủ field, đường ĐỌC nói đúng những gì nó kiểm.
 */
export type ParsedPackManifest = Pick<PackManifest, "version"> &
  Partial<Omit<PackManifest, "version">>;

// Nhận biết object có `version` là chuỗi — điều kiện cần DUY NHẤT, xem lý do ở
// docstring của `ParsedPackManifest`.
function hasStringVersion(value: unknown): value is ParsedPackManifest {
  if (typeof value !== "object" || value === null || !("version" in value)) return false;
  return typeof (value as { version: unknown }).version === "string";
}

/**
 * Đọc manifest từ chuỗi JSON thô.
 *
 * JSON hỏng / không phải object / thiếu `version` ⇒ `null`, KHÔNG throw:
 * workspace vừa `avatar init` mà chưa `sync` là trạng thái HỢP LỆ, không phải lỗi.
 */
export function parsePackManifest(raw: string): ParsedPackManifest | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return hasStringVersion(parsed) ? parsed : null;
}
